import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import store from 'utils/store';
import { SignInWithAmazonV2 } from 'ecommerce/components';
import { prepareAncestryAuthUrl } from 'ecommerce/components/signInWithAncestry';
import { prepareFamilySearchAuthUrl } from 'ecommerce/components/signInWithFamilySearch';
import { authContext } from 'ecommerce/lib/authError';
import useModalContext from 'components/context/modal';
import useDataContext from 'data/context/data';
import useUserContext from 'ecommerce/context/user';

import { getSafeRedirectPath } from 'utils/validator';
import { Head, Layout } from '../components/layout';
import AuthPanel from '../components/auth/authPanel';
import { AuthShell } from '../components/auth/parts';
import authStyles from '../components/auth/auth.module.scss';
import tracker from '../lib/tracker';
import { SESSION_REDIRECT_KEY } from '../lib/sessionKeys';

// Where FamilySearch sign-in lands when the user has no page to return to.
const FAMILYSEARCH_POST_LOGIN_DESTINATION = '/familysearch';

const SignInPage = () => {
  const dataContext = useDataContext();
  const userContext = useUserContext();
  const modalContext = useModalContext();
  const router = useRouter();

  useEffect(() => {
    if (router.asPath.indexOf('/signin') !== 0 && router.asPath.indexOf('/user') !== 0) {
      store.session.set(SESSION_REDIRECT_KEY, router.asPath);
    }
  }, [router.asPath]);

  // Capture redirect target passed from an SSR redirect (e.g. /drafts/:slug → /signin?redirect=...).
  useEffect(() => {
    const redirect = router.query.redirect;
    if (typeof redirect === 'string') {
      const safe = getSafeRedirectPath(redirect, null);
      if (safe) {
        store.session.set(SESSION_REDIRECT_KEY, safe);
      }
    }
  }, [router.query.redirect]);

  const getRedirectUrl = () => {
    if (store.session.has(SESSION_REDIRECT_KEY)) {
      return getSafeRedirectPath(store.session.get(SESSION_REDIRECT_KEY, '/'));
    }
    if (router.asPath.indexOf('/signin') !== 0) {
      return getSafeRedirectPath(router.asPath);
    }
    return '/';
  };

  const setData = async (data, type = 'signin') => {
    userContext.setAccessToken(data.access_token);

    modalContext.toast(
      `You have been successfully ${type === 'signup' ? 'signed up' : 'signed in'}!`,
      {
        type: 'success',
      },
    );

    const redirectUrl = getRedirectUrl();
    store.session.remove(SESSION_REDIRECT_KEY);
    await router.replace(redirectUrl);
    window.scrollTo(0, 0);
  };

  //
  // SIGN IN WITH EMAIL
  //
  const trackEmailError = (errorType, isSignUp) => {
    tracker.onCustomEvent(isSignUp ? 'signup_email_error' : 'signin_email_error', {
      error_type: errorType,
      context: authContext(router.pathname),
    });
  };

  const handleOnSignedIn = async (data, type) => {
    await setData(data, type);
    if (type === 'signup') {
      tracker.signUp(data);
    } else {
      tracker.signIn(data);
    }
  };

  const trackAuthEvent = (action, payload = {}) => {
    tracker.onCustomEvent(action, { ...payload, context: authContext(router.pathname) });
  };

  //
  // SIGN IN WITH ANCESTRY / FAMILYSEARCH
  //
  // The URLs are built on click: prepareFamilySearchAuthUrl reads window.location, which
  // does not exist while this page renders on the server.
  const handleOnAncestryClick = () => {
    window.location = prepareAncestryAuthUrl(getRedirectUrl());
  };

  const handleOnFamilySearchClick = () => {
    const redirectUrl = getRedirectUrl();
    window.location = prepareFamilySearchAuthUrl(
      redirectUrl === '/' ? FAMILYSEARCH_POST_LOGIN_DESTINATION : redirectUrl,
    );
  };

  //
  // SIGN IN WITH AMAZON (v2)
  //
  // SignInWithAmazonV2 already redeems the buyerToken via /api/v1/amazon/buyer
  // and hands us the signed-in user (with access_token) directly.
  const handleOnAmazonSignInSuccess = async (data) => {
    setData(data);
    tracker.signInWithAmazon(data);
  };

  const handleOnAmazonSignInFailed = (error) => {
    tracker.onCustomEvent('signin_amazon_error');
    console.error(error);
    modalContext.toast(
      'Problem with sign in with Amazon. Please try again or use different method.',
      {
        type: 'warning',
      },
    );
  };

  // The Amazon button redeems buyerToken on mount and sits on the options screen only,
  // so an entry ?mode= must not hide it on the return from Amazon.
  const initialMode =
    typeof router.query.mode === 'string' && !router.query.buyerToken
      ? router.query.mode
      : undefined;

  return (
    <>
      <Head title="Login" noindex />

      <Layout className="d-flex">
        <AuthShell>
          <AuthPanel
            initialMode={initialMode}
            countryCode={dataContext.countryCode}
            onAncestry={handleOnAncestryClick}
            onFamilySearch={handleOnFamilySearchClick}
            onSignedIn={handleOnSignedIn}
            onTrackError={trackEmailError}
            onTrack={trackAuthEvent}
            amazon={
              <SignInWithAmazonV2
                redirectUrl="/signin"
                countryCode={dataContext.countryCode}
                onSuccess={handleOnAmazonSignInSuccess}
                onFailed={handleOnAmazonSignInFailed}
                className={authStyles.amazon}
              />
            }
          />
        </AuthShell>
      </Layout>
    </>
  );
};

export default SignInPage;
